// Additional JSON-LD schema helpers (SoftwareApplication, ItemList,
// AggregateRating, HowTo, Person, DefinedTerm). Pair with lib/schemas.ts.

export interface DefinedTermInput {
  term: string;
  englishTerm?: string;
  description: string;
  path: string;
  category?: string;
}

/** DefinedTerm schema — best for glossary entries. Google uses it for featured
 *  snippets and knowledge-panel-style rich results. */
export const definedTermSchema = ({
  term,
  englishTerm,
  description,
  path,
  category,
}: DefinedTermInput): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "DefinedTerm",
  name: term,
  ...(englishTerm ? { alternateName: englishTerm } : {}),
  description,
  // No `inLanguage` here: it is not a valid DefinedTerm property and
  // validators report it as an unexpected property. Language stays on the
  // Article / WebSite nodes, where it is defined.
  url: `https://finansovnavigator.com${path}`,
  ...(category
    ? {
        inDefinedTermSet: {
          "@type": "DefinedTermSet",
          name: "Финансов речник",
          url: "https://finansovnavigator.com/finansov-rechnik",
        },
      }
    : {}),
});

import { AUTHOR_ID, authorRef, cleanSameAs, orgRef } from "./schemas";

const BASE_URL = "https://finansovnavigator.com";
const SITE_NAME = "Финансов Навигатор";
const AUTHOR_NAME = "Алекс В.";
const AUTHOR_URL = `${BASE_URL}/avtor/aleks-vasilev`;

export interface CalculatorSchemaInput {
  name: string;
  description: string;
  path: string;
  category?: string; // e.g. "FinanceApplication"
}

/** SoftwareApplication schema for interactive calculators. Google shows it as a rich result for tool searches. */
export const softwareApplicationSchema = ({
  name,
  description,
  path,
  category = "FinanceApplication",
}: CalculatorSchemaInput): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name,
  description,
  url: `${BASE_URL}${path}`,
  applicationCategory: category,
  operatingSystem: "Any (web-based)",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "EUR",
  },
  inLanguage: "bg-BG",
  publisher: orgRef(),
  // NOTE: deliberately no aggregateRating here. We do not collect user
  // ratings for the calculators, and emitting an invented one violates
  // Google's structured-data guidelines (risk of a manual action).

});

export interface RankingItem {
  name: string;
  path: string; // /kripto-borsi/bybit
  rating: number;
  description?: string;
  logo?: string; // /logos/x.webp
  officialUrl?: string;
}

/** ItemList with embedded Review + AggregateRating — best schema for "Best X 2026" ranking pages. */
export const rankingListSchema = (
  listName: string,
  items: RankingItem[]
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: listName,
  numberOfItems: items.length,
  itemListElement: items.map((item, idx) => ({
    "@type": "ListItem",
    position: idx + 1,
    item: {
      "@type": "FinancialService",
      name: item.name,
      url: `${BASE_URL}${item.path}`,
      ...(item.logo ? { image: `${BASE_URL}${item.logo}` } : {}),
      ...(item.officialUrl && cleanSameAs([item.officialUrl]).length
        ? { sameAs: cleanSameAs([item.officialUrl]) }
        : {}),
      ...(item.description ? { description: item.description } : {}),
      // No aggregateRating: we store no user votes, so any rating count
      // here would be invented. The editorial score stays as a single
      // Review by our named author instead.
      review: {
        "@type": "Review",
        author: authorRef(),
        reviewRating: {
          "@type": "Rating",
          ratingValue: item.rating.toFixed(1),
          bestRating: "5",
          worstRating: "1",
        },
        publisher: orgRef(),
      },
    },
  })),
});

export interface HowToStep {
  name: string;
  text: string;
}

export const howToSchema = (
  name: string,
  description: string,
  steps: HowToStep[],
  path: string
): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  name,
  description,
  url: `${BASE_URL}${path}`,
  inLanguage: "bg-BG",
  step: steps.map((s, i) => ({
    "@type": "HowToStep",
    position: i + 1,
    name: s.name,
    text: s.text,
  })),
});

/** Person schema for author E-E-A-T pages. */
export const personSchema = (): Record<string, unknown> => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": AUTHOR_ID,
  name: AUTHOR_NAME,
  url: AUTHOR_URL,
  jobTitle: "Финансов анализатор и редактор",
  worksFor: orgRef(),
  knowsAbout: [
    "Инвестиционни платформи",
    "Крипто борси",
    "MiCA регулация",
    "Лични финанси",
    "Банкови продукти",
    "Финансови калкулатори",
  ],
  description:
    "Финансов анализатор с фокус върху сравнения на инвестиционни платформи, крипто борси и банкови продукти за българския пазар.",
});
